import { eventChannel, EventChannel } from "redux-saga";
import { call, fork, put, select, take } from "redux-saga/effects";

import { createFileAttachmentUrl } from "@/lib/api/proposal-api";

import { RootState } from "../../rootReducer";
import { ProposalSlideDefinition } from "../../types";
import deckSlice, { changeSlideBackground } from "../deck/deck.slice";
import { fetchProposalData } from "../proposal/proposal.actions";
import { initPreview } from "../proposal/proposal.slice";
import { fetchProposalTemplate, updateSlideConfig } from "../template";

type ScreenPreviewEventAction = { type: "preview"; slide: ProposalSlideDefinition };

function* fetchTemplateSaga(proposalId: string) {
  yield put(yield call(fetchProposalTemplate, proposalId));
}

function* fetchProposalSaga(proposalId: string) {
  yield put(yield call(fetchProposalData, proposalId));
}

function* updateSlideConfigFromEventChannel(action: ScreenPreviewEventAction) {
  const isDeckReady = yield select((state: RootState) => state.deck.ready);

  if (!isDeckReady) {
    // wait for initial template load
    yield take(deckSlice.actions.ready);
  }

  if (action.slide.config?.backgroundType === "video" && action.slide.config?.backgroundFile) {
    const videoUrl = createFileAttachmentUrl("demo", action.slide.config?.backgroundFile);
    yield put(
      changeSlideBackground({
        id: action.slide.id!,
        videoUrl,
        backgroundType: "video",
      })
    );
  }

  if (action.slide.config?.backgroundType === "image" && action.slide.config?.backgroundFile) {
    const imageUrl = createFileAttachmentUrl("demo", action.slide.config?.backgroundFile);
    yield put(
      changeSlideBackground({
        id: action.slide.id!,
        imageUrl,
        backgroundType: "image",
      })
    );
  }

  yield put(updateSlideConfig(action.slide));
}

export function* proposalPreviewSaga() {
  const initAction: ReturnType<typeof initPreview> = yield take(initPreview.type);
  const proposalId = initAction.payload.id;

  yield fork(fetchProposalSaga, proposalId);
  yield fork(fetchTemplateSaga, proposalId);

  const iframeChannel = yield call(createMessageEventChannel);

  yield fork(watchIframeMessages, iframeChannel, updateSlideConfigFromEventChannel);
  window.parent.postMessage({ type: "loaded" }, "*");
}

function createMessageEventChannel() {
  return eventChannel(emit => {
    const listener = (ev: MessageEvent<any>) => {
      if (!ev.isTrusted) return;
      if (ev.data.type !== "preview") return;
      emit(ev.data);
    };

    window.addEventListener("message", listener);

    // the subscriber must return an unsubscribe function
    // this will be invoked when the saga calls `channel.close` method
    const unsubscribe = () => {
      window.removeEventListener("message", listener);
    };

    return unsubscribe;
  });
}

function* watchIframeMessages(
  iframeChannel: EventChannel<ScreenPreviewEventAction>,
  cb: (payload: ScreenPreviewEventAction) => void
) {
  while (true) {
    try {
      // Subscribe to event payloads from preview iframe
      const payload: any = yield take(iframeChannel);
      yield call(cb, payload);
    } catch (err) {
      console.error("iframe error:", err);
      // iframeChannel is still open in catch block
      // if we want end the iframeChannel, we need close it explicitly
      // iframeChannel.close()
    }
  }
}
